import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import type { CastState, ClassState } from '../../../shared/game/class-types.js';
import type { AttackDefinition } from '../../../shared/game/combat-types.js';
import type { EnemyState, UnitState } from '../../../shared/game/world-types.js';

/** Monster attacks that are spells, and the channel each is cast with (an ability id `schoolOfAbility` reads as the school). */
export const MONSTER_SPELLS: Readonly<Record<string, string>> = { 'bandit-arc': 'channel-lightning' };
export const isMonsterSpell = (attackId: string) => Object.hasOwn(MONSTER_SPELLS, attackId);
/** How long a channel that has come due waits for its shot before it lapses. */
const LAPSE_SECONDS = .4;

interface Channel { abilityId: string; targetId: string; endsAt: number; duration: number; shotAt?: number; endedAt?: number; releasedAt?: number }
interface Track { channel?: Channel }

/**
 * Presentation-only spellcasting for monsters. A monster's attack is a windup on the server (`MONSTER_ACTION_SECONDS`) and the spell leaves at its
 * end, so over that windup the monster is given a projected cast, which the animator and `SpellChannels` turn into the same orb, hands and channel
 * sounds a hero's spell has. The channel lets go when its shot appears (the discharge starts at the orb) and ends without a spell if the
 * monster loses its target, falls, is Dazed or Prone, or its windup is otherwise cancelled.
 *
 * Snapshots arrive about 100 ms ahead of what is drawn, so each channel is kept on the game clock and read at the rendered game time.
 */
export class MonsterChannels {
  private readonly tracks = new Map<string, Track>();
  private revision = -1;
  private projectileId = 0;
  private lastTime = 0;
  /** `attacks` names a unit's attacks (an enemy definition's), or undefined for a unit that has none. */
  constructor(private readonly attacks: (id: string) => readonly Pick<AttackDefinition, 'id' | 'range'>[] | undefined) {}

  private spell(enemy: EnemyState) {
    const attacks = this.attacks(enemy.id)?.filter(attack => isMonsterSpell(attack.id));
    return attacks?.find(attack => attack.id === enemy.combat.attackId) ?? attacks?.[0];
  }

  receive(snapshot: Pick<WorldSnapshot, 'revision' | 'gameTime' | 'inCombat' | 'heroes' | 'enemies' | 'projectiles'>) {
    if (snapshot.revision !== this.revision) {
      this.clear(); this.revision = snapshot.revision; this.lastTime = snapshot.gameTime;
      this.projectileId = Math.max(0, ...snapshot.projectiles.map(shot => shot.id));
    }
    const shooters = new Set(snapshot.projectiles.filter(shot => shot.id > this.projectileId && isMonsterSpell(shot.attackId)).map(shot => shot.actorId));
    this.projectileId = Math.max(this.projectileId, ...snapshot.projectiles.map(shot => shot.id));
    const units = new Map<string, UnitState>([...snapshot.heroes, ...snapshot.enemies].map(unit => [unit.id, unit]));
    for (const id of this.tracks.keys()) if (!units.has(id)) this.tracks.delete(id);
    for (const enemy of snapshot.enemies) {
      const spell = this.spell(enemy);
      if (!spell) continue;
      let track = this.tracks.get(enemy.id);
      if (!track) this.tracks.set(enemy.id, track = {});
      const shot = shooters.has(enemy.id);
      let open = track.channel && track.channel.shotAt === undefined && track.channel.endedAt === undefined ? track.channel : undefined;
      // The shot is drawn as soon as its snapshot is the one being interpolated toward: the orb lets go then too.
      if (open && shot) { open.shotAt = this.lastTime; open = undefined; }
      const windup = enemy.combat.windup, target = windup?.targetId ? units.get(windup.targetId) : undefined;
      const ready = snapshot.inCombat && !enemy.health.dead && !enemy.health.dying && !enemy.fleeing && !enemy.escaped && windup?.kind === 'attack' && windup.attackId === spell.id
        && !!target && !target.health.dead;
      if (ready && open) { open.endsAt = snapshot.gameTime + windup.remaining; open.targetId = target.id; }
      else if (ready && !shot) track.channel = { abilityId: MONSTER_SPELLS[spell.id], targetId: target.id, endsAt: snapshot.gameTime + windup.remaining, duration: windup.duration };
      else if (!ready && open) open.endedAt = this.lastTime;
    }
    this.lastTime = snapshot.gameTime;
  }

  /** The cast a monster shows at rendered game time `gameTime`, if it is channeling. The frame its shot appears it is complete; after that it is gone. */
  castAt(id: string, gameTime: number): CastState | undefined {
    const channel = this.tracks.get(id)?.channel;
    if (!channel || gameTime < channel.endsAt - channel.duration) return undefined;
    if (channel.endedAt !== undefined && gameTime >= channel.endedAt) return undefined;
    const cast = (remaining: number): CastState => ({ abilityId: channel.abilityId, targetId: channel.targetId, duration: channel.duration, remaining });
    if (channel.shotAt !== undefined && gameTime >= channel.shotAt) {
      channel.releasedAt ??= gameTime;
      return gameTime > channel.releasedAt ? undefined : cast(0);
    }
    if (channel.shotAt === undefined && gameTime > channel.endsAt + LAPSE_SECONDS) return undefined;
    // Held just short of complete until the shot is seen, so the cast is only finished by a spell.
    return cast(Math.min(channel.duration, Math.max(channel.duration * .01, channel.endsAt - gameTime)));
  }

  /** `state` as the animator should see it: with the projected cast while the monster channels. */
  project<T extends UnitState>(state: T, gameTime: number): T {
    if ('class' in state) return state;
    const casting = this.castAt(state.id, gameTime);
    if (!casting) return state;
    const shown: ClassState = { classId: '', level: 0, abilities: [], quarryId: null, casting };
    return { ...state, class: shown };
  }

  clear() { this.tracks.clear(); }
}
